import { ChangeDetectorRef, Component, DestroyRef, Input, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { catchError, distinctUntilChanged, EMPTY, filter, map, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfigService } from '../../services/config/config.service';
import { ConfigGlobalModel } from 'app/models/config-global-model';
import { LandingLocation, LandingPageConfig, LandingPageLocker } from './landing-page.models';
import { MYLOCKER_LANDING_CONFIG } from './landing-page.config';
import { LandingPageDataService } from './landing-page-data.service';
import { LanguageService } from '../../services/language/language.service';
import { isSupportedLanguage } from '../../services/language/languages';
import { AppService } from '../../services/app/app.service';
import { SeoService } from '../../services/seo/seo.service';

const LOCKER_FALLBACK_TEXT: Record<string, { NAME: string; DESCRIPTION: string }> = {
  S: { NAME: 'landing.locker.smallName', DESCRIPTION: 'landing.locker.smallDescription' },
  M: { NAME: 'landing.locker.mediumName', DESCRIPTION: 'landing.locker.mediumDescription' },
  XL: { NAME: 'landing.locker.largeName', DESCRIPTION: 'landing.locker.largeDescription' },
};

const FALLBACK_FAQ_KEYS: Record<string, string> = {
  'tiempo-reserva': 'duration',
  ubicacion: 'location',
  'limite-peso': 'size',
  'metodos-pago': 'payment',
  'modificar-reserva': 'change',
  seguridad: 'safety',
};

const FALLBACK_REVIEW_KEYS: Record<string, string> = {
  'ana-m': 'ana',
  'carlos-r': 'carlos',
  'laura-g': 'laura',
};

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  standalone: false
})
export class LandingPageComponent implements OnInit {
  readonly globalConfig: ConfigGlobalModel;
  readonly locations: LandingLocation[];
  readonly whatsappUrl: string;

  constructor(
    configService: ConfigService,
    private route: ActivatedRoute,
    public language: LanguageService,
    private appService: AppService,
    private seo: SeoService,
    private landingPageData: LandingPageDataService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {
    this.globalConfig = configService.settings;
    const phone = (this.globalConfig.phone || '').replace(/\D/g, '');
    this.whatsappUrl = `https://wa.me/${phone.length === 9 ? `34${phone}` : phone}`;
    this.locations = (this.globalConfig.locations ?? [])
      //.filter(location => Number.isFinite(location.latitude) && Number.isFinite(location.longitude))
      .map(location => ({
        id: String(location.id),
        code: location.code || location.name,
        name: location.name,
        address: location.address ?? '',
        latitude: Number(location.latitude)!,
        longitude: Number(location.longitude)!,
      }));
    this.config = { ...this.localizedFallbackConfig(), ...this.landingPageData.initialConfig };
    this.seo.updateLanding(this.config, this.globalConfig);
  }

  @Input() config: LandingPageConfig;

  readonly menuOpen = signal(false);

  ngOnInit(): void {
    this.route.parent!.paramMap.pipe(
      map(params => params.get('lang')),
      filter(isSupportedLanguage),
      distinctUntilChanged(),
      switchMap(lang => this.landingPageData.load(lang).pipe(
        catchError(error => {
          console.error('No se pudo cargar la landing', error);
          return EMPTY;
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: result => {
        this.config = { ...this.localizedFallbackConfig(), ...result };
        this.seo.updateLanding(this.config, this.globalConfig);
        this.cdr.markForCheck();
      },
    });
  }

  lockerImage(locker: LandingPageLocker): string {
    return locker.image?.imageMedium
      || locker.image?.imageLarge
      || locker.image?.imageSmall
      || `assets/landing/locker-${['s', 'm', 'xl'].includes(locker.code?.toLowerCase()) ? locker.code.toLowerCase() : 'm'}.jpg`;
  }

  lockerLiteral(locker: LandingPageLocker, property: 'NAME' | 'DESCRIPTION'): string {
    const code = locker.code?.toUpperCase();
    const literals = Array.isArray(locker.literals) ? locker.literals : [];
    const literal = literals.find(item => item?.key === `LANDING_PAGE_LOCKER_${code}_${property}`)
      ?? literals.find(item => item?.key?.endsWith(`_${property}`));

    if (literal?.value && literal.value !== literal.key) {
      return literal.value;
    }

    const key = LOCKER_FALLBACK_TEXT[code]?.[property];
    return key ? this.appService.getTraduccion(key) : '';
  }

  private localizedFallbackConfig(): LandingPageConfig {
    return {
      ...MYLOCKER_LANDING_CONFIG,
      title: this.appService.getTraduccion('landing.fallback.title'),
      reviews: MYLOCKER_LANDING_CONFIG.reviews.map(review => ({
        ...review,
        text: FALLBACK_REVIEW_KEYS[review.id]
          ? this.appService.getTraduccion(`landing.fallback.review.${FALLBACK_REVIEW_KEYS[review.id]}`)
          : review.text,
      })),
      faqs: MYLOCKER_LANDING_CONFIG.faqs.map(faq => {
        const key = FALLBACK_FAQ_KEYS[faq.id];
        if (!key) return faq;
        return {
          ...faq,
          question: this.appService.getTraduccion(`landing.fallback.faq.${key}Question`),
          answer: this.appService.getTraduccion(`landing.fallback.faq.${key}Answer`),
        };
      }),
    };
  }

  toggleMenu(): void {
    this.menuOpen.update((value) => !value);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

}
